import { useEffect, useRef, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { representDocuments, type RepresentDocument } from './representData';
import { PdfReaderModal } from './PdfReaderModal';

const useScrollReveal = (ref: React.RefObject<HTMLDivElement | null>) => {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('visible');
        }),
      { threshold: 0.05 }
    );
    ref.current?.querySelectorAll('.animate-on-scroll').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
};

const Represent = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  const [activeDoc, setActiveDoc] = useState<RepresentDocument | null>(null);

  return (
    <div className="font-jakarta bg-white min-h-screen flex flex-col">
      <Navbar />

      <main ref={pageRef} className="pt-20 flex-1 flex flex-col">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-emerald-50/50 via-white to-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-12 text-center">
            <div className="animate-on-scroll mb-4">
              <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">
                Official Representations &amp; Advocacy
              </span>
            </div>

            <h1 className="animate-on-scroll text-4xl sm:text-5xl md:text-6xl font-bold text-ink leading-tight tracking-tight mb-5">
              BEA Represent
            </h1>

            <p className="animate-on-scroll text-ink-muted text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Official memorandums, policy whitepapers, regulatory submissions, and tender annexures
              championing Bihar&apos;s grain-based ethanol industry before central and state authorities.
            </p>
          </div>
        </section>

        {/* Documents Directory Section */}
        <section className="bg-surface flex-1 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
            {/* Documents Tile Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {representDocuments.map((doc) => {
                const categoryBadgeStyles = {
                  representation: 'bg-emerald-50 text-emerald-800 border-emerald-200/80',
                  whitepaper: 'bg-purple-50 text-purple-800 border-purple-200/80',
                  annexure: 'bg-amber-50 text-amber-900 border-amber-200/80',
                }[doc.category];

                return (
                  <div
                    key={doc.id}
                    onClick={() => setActiveDoc(doc)}
                    className="group bg-white rounded-2xl border border-gray-200/90 shadow-2xs hover:shadow-xl hover:border-bteal/50 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between overflow-hidden cursor-pointer relative"
                  >
                    {/* Top Accent Strip */}
                    <div
                      className={`h-1.5 w-full ${
                        doc.category === 'representation'
                          ? 'bg-bteal'
                          : doc.category === 'whitepaper'
                          ? 'bg-purple-600'
                          : 'bg-amber-500'
                      }`}
                    />

                    <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between">
                      <div>
                        {/* Header Badges */}
                        <div className="flex items-center justify-between gap-2 mb-3.5">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${categoryBadgeStyles}`}
                          >
                            {doc.categoryLabel}
                          </span>
                          <span className="text-xs font-semibold text-ink-muted flex items-center gap-1">
                            <i className="ri-calendar-line text-gray-400 text-xs" />
                            {doc.date}
                          </span>
                        </div>

                        {/* Title & Document Header */}
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                            <i className="ri-file-pdf-2-line text-xl" />
                          </div>
                          <div className="min-w-0">
                            <h3
                              className="font-bold text-ink text-base sm:text-lg leading-snug group-hover:text-bteal transition-colors line-clamp-2"
                              title={doc.title}
                            >
                              {doc.title}
                            </h3>
                            {doc.referenceNo && (
                              <p className="text-[11px] font-mono text-gray-400 truncate mt-0.5">
                                {doc.referenceNo}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Summary */}
                        <p className="text-ink-muted text-xs sm:text-sm leading-relaxed mb-4 line-clamp-3">
                          {doc.summary}
                        </p>
                      </div>

                      {/* Authority & Knowledge Hub Style Download CTA */}
                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-1.5 min-w-0 text-ink-muted">
                          <i className="ri-building-line text-bteal shrink-0" />
                          <span className="font-semibold text-ink text-[11px] truncate" title={doc.authority}>
                            {doc.authority}
                          </span>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (doc.pdfUrl && doc.pdfUrl !== '#') {
                              window.open(doc.pdfUrl, '_blank');
                            }
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-bteal text-bteal text-xs font-semibold hover:bg-bteal hover:text-white transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0"
                          title="Open document in new tab"
                        >
                          <i className="ri-download-line text-xs" />
                          <span>Download</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Advocacy Commitment Note */}
        <section className="bg-white py-12 border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="rounded-2xl p-6 md:p-8 bg-gradient-to-r from-bteal/5 via-emerald-50/50 to-white border border-bteal/20 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-bteal/10 text-bteal flex items-center justify-center shrink-0 mt-1">
                  <i className="ri-scales-3-line text-2xl" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-ink mb-1">
                    Committed to Transparent Policy Dialogue
                  </h3>
                  <p className="text-xs sm:text-sm text-ink-muted leading-relaxed max-w-2xl">
                    The Bihar Ethanol Association acts as the official institutional bridge between grain-based
                    distilleries, the State Industries &amp; Excise Departments, Oil Marketing Companies (OMCs), and the
                    Ministry of Petroleum and Natural Gas (MoPNG).
                  </p>
                </div>
              </div>
              <a
                href="mailto:ethanolassociationbihar@gmail.com"
                className="px-5 py-2.5 rounded-xl bg-ink text-white hover:bg-bteal text-xs font-semibold transition-colors shadow-2xs whitespace-nowrap shrink-0 flex items-center gap-2"
              >
                <i className="ri-mail-send-line text-sm" />
                <span>Contact BEA Secretariat</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Pop-up Interactive PDF Reader Modal */}
      <PdfReaderModal
        document={activeDoc}
        allDocuments={representDocuments}
        onClose={() => setActiveDoc(null)}
        onSelectDocument={setActiveDoc}
      />

      <Footer />
    </div>
  );
};

export default Represent;
